/**
 * RunningCosts
 *
 * Daily variable cost entry for the substrate operation.
 * Each cost is linked to a specific purchase/container.
 */

import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, Trash2, Loader2, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { RUNNING_COST_CATEGORIES } from '@/hooks/useSubstrateLogs'
import { toLocalDateString } from '@/utils/dateUtils'

const CATEGORY_LABELS = {
  energy: 'Energy',
  maintenance: 'Maintenance',
  consumables: 'Consumables',
  labour: 'Labour',
  other: 'Other'
}

export default function RunningCosts({ hook }) {
  const { runningCosts, purchases, addRunningCost, deleteRunningCost, isLoading } = hook

  const [newItem, setNewItem] = useState({
    purchase_id: '',
    cost_date: toLocalDateString(new Date()),
    category: 'energy',
    description: '',
    amount: ''
  })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null)

  const allTotal = useMemo(() => {
    return runningCosts.reduce((sum, rc) => sum + Number(rc.amount), 0)
  }, [runningCosts])

  // Default to the first purchase if only one exists
  const effectivePurchaseId = newItem.purchase_id || (purchases.length === 1 ? purchases[0].id : '')

  const handleAdd = async () => {
    if (!newItem.amount || parseFloat(newItem.amount) <= 0) return
    if (!effectivePurchaseId) return

    setSaving(true)
    try {
      await addRunningCost({
        purchase_id: effectivePurchaseId,
        cost_date: newItem.cost_date,
        category: newItem.category,
        description: newItem.description || CATEGORY_LABELS[newItem.category],
        amount: parseFloat(newItem.amount)
      })
      setNewItem(prev => ({
        ...prev,
        description: '',
        amount: ''
      }))
    } catch (err) {
      console.error('Failed to add running cost:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    setDeleting(id)
    try {
      await deleteRunningCost(id)
    } catch (err) {
      console.error('Failed to delete running cost:', err)
    } finally {
      setDeleting(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Running Costs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add New Row */}
          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-36">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={newItem.cost_date}
                onChange={(e) => setNewItem(prev => ({ ...prev, cost_date: e.target.value }))}
              />
            </div>
            {purchases.length > 1 && (
              <div className="w-48">
                <Label className="text-xs">Purchase</Label>
                <Select value={effectivePurchaseId} onValueChange={(val) => setNewItem(prev => ({ ...prev, purchase_id: val }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select purchase..." />
                  </SelectTrigger>
                  <SelectContent>
                    {purchases.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.purchase_code} — {p.supplier_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="w-40">
              <Label className="text-xs">Category</Label>
              <Select value={newItem.category} onValueChange={(val) => setNewItem(prev => ({ ...prev, category: val }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RUNNING_COST_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <Label className="text-xs">Description</Label>
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description..."
              />
            </div>
            <div className="w-32">
              <Label className="text-xs">Amount ({'\u00A3'})</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={newItem.amount}
                onChange={(e) => setNewItem(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <Button onClick={handleAdd} disabled={saving || !newItem.amount || !effectivePurchaseId} size="sm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>

          {/* Purchase hint when only one exists */}
          {purchases.length === 1 && (
            <p className="text-xs text-muted-foreground">
              Linked to {purchases[0].purchase_code} — {purchases[0].supplier_name}
            </p>
          )}

          {/* All Items */}
          {runningCosts.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              No running costs recorded. Add items above.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Purchase</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runningCosts.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-muted-foreground">
                      {new Date(item.cost_date + 'T00:00:00').toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.substrate_log_purchases?.purchase_code || '—'}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{CATEGORY_LABELS[item.category] || item.category}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        {item.description}
                        {item.source === 'automated' && (
                          <Badge variant="outline" className="text-xs px-1 py-0">
                            <Zap className="h-3 w-3 mr-0.5" />auto
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {'\u00A3'}{Number(item.amount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        disabled={deleting === item.id}
                      >
                        {deleting === item.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-destructive" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <tfoot>
                <TableRow className="font-medium border-t">
                  <TableCell colSpan={4}>Total</TableCell>
                  <TableCell className="text-right">{'\u00A3'}{allTotal.toFixed(2)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </tfoot>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
